import { ReactNode } from 'react';

import { SpinnerIcon } from '@tg-web/icons';
import { PropsWithCN, cn } from '@tg-web/utils';

import { ISLAND_RADIUS_CLASSNAME } from './Island';

export type IslandSegment = {
  icon: ReactNode;
  /**
   * The icon's own colour, as a text-colour class. Every segment wears it while
   * nothing is chosen; once something is, only the chosen one keeps it.
   */
  iconClassName: string;
  /** Identifies the segment, and is what `onSelect` hands back. */
  key: string;
  label: string;
  /**
   * This segment's write is in flight: its icon becomes a spinner and it stops
   * accepting taps. Nothing here is optimistic, so the container keeps showing
   * the old `value` until the refetch lands.
   */
  pending?: boolean;
};

export type IslandSegmentedCardProps = PropsWithCN & {
  onSelect: (key: string) => void;
  segments: IslandSegment[];
  /** The chosen segment's key, or nothing while the choice is unmade. */
  value?: string;
};

/**
 * A mutually-exclusive choice laid out across the island, rather than down it.
 *
 * This is the one island container that is not a vertical stack of full-width
 * rows, and it is deliberately not built on `Island`: `islandClassName`
 * hardcodes `flex-col`, so wrapping would mean overriding the one thing the
 * container exists to declare. It borrows the chrome instead — same radius,
 * same `bg-tg-section-bg`, same `blur-shadow-small` — so it reads as part of
 * the family.
 *
 * Two container states, and they are inverses of each other:
 *
 * - **unmade** — the container is the island, and every icon wears its own
 *   colour: three equal offers, none of them answered.
 * - **made** — the container turns transparent and keeps only a hairline
 *   ring, and the chosen segment holds a card of its own, inset 4px and at
 *   radius 20 so it sits concentric with the container's 24. The chosen icon
 *   keeps its colour; the other two drop to `tg-text`, because they are no
 *   longer offers, they are the way back. The inset card carries no shadow --
 *   the design draws it flat, inside the border.
 *
 * `IslandRadioCell` is the nearest relative and the reason this is a
 * `radiogroup` of real buttons rather than divs — but it solves its tap targets
 * by being a `<label>` around a Radix control, and there is no control to wrap
 * here. The visual *is* the state.
 */
export function IslandSegmentedCard({
  className,
  onSelect,
  segments,
  value,
}: IslandSegmentedCardProps) {
  const activeIndex = segments.findIndex((segment) => segment.key === value);
  const isPicked = activeIndex !== -1;

  return (
    <div
      className={cn(
        // 86px is the card, and the 54px separators are drawn against it, so
        // the height is fixed rather than derived from the segments.
        //
        // No gap: the three segments fill the card edge to edge, so each
        // segment *is* the visual cell between an edge and a separator, and
        // its content is centred in exactly that. A gap between segments with
        // the separator in its middle left the outer cells half a gap wider
        // on the card side than the segment their content is centred in.
        'relative flex h-[86px] w-full shrink-0',
        ISLAND_RADIUS_CLASSNAME,
        // The picked hairline is a ring, not a border: a border takes layout
        // space, which would start the segments half its width inside the
        // card edge and put the outer cells' content off their centres.
        isPicked
          ? 'event-surface-picked ring-tg-section-separator ring-[0.33px] ring-inset'
          : 'event-surface bg-tg-section-bg blur-shadow-small',
        className,
      )}
      role="radiogroup"
    >
      {segments.map((segment, index) => {
        const isActive = index === activeIndex;

        return (
          <button
            className={cn(
              // flex-1 with basis-0 and min-w-0: nothing a label does can make
              // one segment wider than the others. Without min-w-0 a label
              // wider than its segment sets the segment's minimum width, and
              // the separators -- at fixed thirds -- stop landing on the
              // boundaries. Side padding is 4 rather than 16: everything in a
              // segment is centred, so it only ever cost a long label room.
              'relative flex h-full min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1.5 px-1 py-4 transition-colors',
              ISLAND_RADIUS_CLASSNAME,
              // The chosen answer is not something to press again, so it
              // does not look pressable either.
              segment.pending || isActive
                ? 'cursor-default'
                : 'cursor-pointer active:bg-black/[0.06]',
            )}
            onClick={() => {
              // A checked radio does nothing when chosen again.
              if (!isActive) {
                onSelect(segment.key);
              }
            }}
            aria-checked={isActive}
            disabled={segment.pending}
            key={segment.key}
            role="radio"
            type="button"
          >
            {/*
             * Inside the segment and inset 4 on every side, so it sits
             * symmetric in its cell and moves nothing around it.
             */}
            {isActive && (
              <span
                className="event-surface bg-tg-section-bg absolute inset-1 rounded-[20px]"
                aria-hidden
                data-active-card
              />
            )}
            <span
              className={cn(
                'relative flex h-6 w-6 shrink-0 items-center justify-center',
                !isPicked || isActive ? segment.iconClassName : 'text-tg-text',
              )}
              data-segment-icon
            >
              {segment.pending ? (
                <SpinnerIcon className="h-5 w-5 animate-spin" />
              ) : (
                segment.icon
              )}
            </span>
            <span className="typo-feed text-tg-text relative text-center">
              {segment.label}
            </span>
          </button>
        );
      })}
      {/*
       * Laid over the row rather than in it, exactly on the segment
       * boundaries, and centred on the line: in the flow they took width and
       * pushed the cells apart. 54 tall and vertically centred in the 86.
       *
       * One touching the chosen segment goes clear rather than away, so the
       * pair is always there to measure against: a line running into the
       * inset card reads as a seam.
       */}
      {segments.slice(1).map((segment, index) => {
        const boundary = index + 1;
        const touchesActive =
          boundary === activeIndex || boundary - 1 === activeIndex;

        return (
          <div
            className={cn(
              'border-l-03 absolute top-1/2 h-[54px] w-0 -translate-x-1/2 -translate-y-1/2',
              touchesActive
                ? 'border-transparent'
                : 'border-tg-section-separator',
            )}
            key={`separator-before-${segment.key}`}
            style={{ left: `${(boundary / segments.length) * 100}%` }}
            aria-hidden
            data-separator
          />
        );
      })}
    </div>
  );
}
